import React from "react";
import { Badge, Button, Callout, Card, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { ID, Moodboard, MoodboardsOverview, StyleProfile } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

export function MoodboardsScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [overview, setOverview] = React.useState<MoodboardsOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [overviewError, setOverviewError] = React.useState<string | null>(null);
  const [addingBoard, setAddingBoard] = React.useState(false);
  const [uploadingFor, setUploadingFor] = React.useState<Moodboard | null>(null);
  const [editingStyle, setEditingStyle] = React.useState<{ eventId?: ID; profile?: StyleProfile } | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      setOverview(await service.moodboards.list());
    } catch (err) {
      setOverviewError(errorMessage(err));
    } finally {
      setOverviewLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading moodboards" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const deleteBoard = async (boardId: ID) => {
    setActionError(null);
    try {
      await service.moodboards.deleteBoard(boardId);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const deleteImage = async (boardId: ID, imageId: ID) => {
    setActionError(null);
    try {
      await service.moodboards.deleteImage(boardId, imageId);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const wholeWeddingProfile = overview?.styleProfiles.find((p) => !p.eventId);

  return (
    <div>
      <PageHeader eyebrow="Design" title="Moodboards" meta={[{ icon: "image", label: "Wedding style lives here too" }]} actions={<Button variant="primary" icon="plus" onClick={() => setAddingBoard(true)}>New board</Button>} />
      <div className="pob-pad">
        {overviewLoading && !overview && <LoadingState label="Loading moodboards" />}
        {overviewError && <ErrorState message={overviewError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}

        {overview && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            <Card eyebrow="Wedding style" title="Palette & style tags" action={<Button size="sm" onClick={() => setEditingStyle({ profile: wholeWeddingProfile })}>Edit</Button>}>
              <StyleProfileSummary profile={wholeWeddingProfile} />
            </Card>

            {workspace.events.map((event) => {
              const profile = overview.styleProfiles.find((p) => p.eventId === event.id);
              if (!profile) return null;
              return (
                <Card key={event.id} eyebrow={event.name} title="Celebration style" action={<Button size="sm" onClick={() => setEditingStyle({ eventId: event.id, profile })}>Edit</Button>}>
                  <StyleProfileSummary profile={profile} />
                </Card>
              );
            })}
            <Button size="sm" onClick={() => setEditingStyle({})}>Add a per-celebration style</Button>

            {overview.boards.map((board) => (
              <Card key={board.id} eyebrow={board.eventId ? workspace.events.find((e) => e.id === board.eventId)?.name || "Celebration" : "Whole wedding"} title={board.name} action={<span style={{ display: "flex", gap: "var(--space-2)" }}><Button size="sm" onClick={() => setUploadingFor(board)}>Add image</Button><Button size="sm" variant="danger" onClick={() => deleteBoard(board.id)}>Remove board</Button></span>}>
                {board.notes && <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)", marginBottom: "var(--space-3)" }}>{board.notes}</p>}
                {board.images.length === 0 ? (
                  <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No images yet.</span>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "var(--space-3)" }}>
                    {board.images.map((image) => (
                      <div key={image.id} style={{ position: "relative", borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border-subtle)" }}>
                        {image.viewUrl ? <img src={image.viewUrl} alt={image.caption || "Moodboard image"} style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} /> : <div style={{ width: "100%", height: 140, background: "var(--surface-sunken)" }} />}
                        <button type="button" onClick={() => deleteImage(board.id, image.id)} aria-label="Remove image" style={{ position: "absolute", top: 4, right: 4, border: 0, borderRadius: "50%", width: 24, height: 24, background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer" }}>×</button>
                        {image.caption && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "4px 6px", background: "rgba(0,0,0,0.55)", color: "#fff", font: "var(--type-body-sm)", fontSize: "var(--text-xs)" }}>{image.caption}</div>}
                        {image.tags.length > 0 && <div style={{ position: "absolute", top: 4, left: 4, display: "flex", gap: 4 }}>{image.tags.slice(0, 2).map((t) => <Badge key={t} tone="neutral">{t}</Badge>)}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {addingBoard && <BoardDialog onClose={() => setAddingBoard(false)} onSaved={async () => { setAddingBoard(false); await load(); }} />}
      {uploadingFor && <UploadImageDialog board={uploadingFor} onClose={() => setUploadingFor(null)} onSaved={async () => { setUploadingFor(null); await load(); }} />}
      {editingStyle && <StyleProfileDialog eventId={editingStyle.eventId} profile={editingStyle.profile} onClose={() => setEditingStyle(null)} onSaved={async () => { setEditingStyle(null); await load(); }} />}
    </div>
  );
}

function StyleProfileSummary({ profile }: { profile?: StyleProfile }) {
  if (!profile || (profile.paletteColors.length === 0 && profile.styleTags.length === 0 && !profile.notes)) {
    return <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>Nothing set yet.</span>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      {profile.paletteColors.length > 0 && (
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          {profile.paletteColors.map((color) => <span key={color} title={color} style={{ width: 28, height: 28, borderRadius: "50%", background: color, border: "1px solid var(--border-subtle)" }} />)}
        </div>
      )}
      {profile.styleTags.length > 0 && <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>{profile.styleTags.map((tag) => <Badge key={tag} tone="neutral">{tag}</Badge>)}</div>}
      {profile.notes && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{profile.notes}</span>}
    </div>
  );
}

function BoardDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [name, setName] = React.useState("");
  const [eventId, setEventId] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!name.trim()) { setFormError("Give the board a name."); return; }
    try {
      await service.moodboards.createBoard({ name: name.trim(), eventId: eventId || undefined, notes: notes || undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog title="New moodboard" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Creating…" : "Create"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Reception decor" /></Field>
        <Field label="Celebration (optional)"><Select value={eventId} placeholder="Whole wedding" onChange={(e) => setEventId(e.target.value)} options={workspace!.events.map((ev) => ({ value: ev.id, label: ev.name }))} /></Field>
        <Field label="Notes"><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}

function UploadImageDialog({ board, onClose, onSaved }: { board: Moodboard; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [file, setFile] = React.useState<File | null>(null);
  const [caption, setCaption] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [fileError, setFileError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const handleFile = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) return;
    if (!ALLOWED_IMAGE_TYPES.includes(selected.type)) { setFileError("Use a JPEG, PNG, WebP or GIF image."); setFile(null); return; }
    if (selected.size > MAX_IMAGE_BYTES) { setFileError("That image is larger than the 15 MB limit."); setFile(null); return; }
    setFileError(null);
    setFile(selected);
  };

  const submit = () => guardedSubmit(async () => {
    if (!file) return;
    try {
      await service.moodboards.uploadImage(board.id, file, { caption: caption || undefined, tags: tags ? tags.split(",").map((t) => t.trim()).filter(Boolean) : undefined });
      await onSaved();
    } catch (err) {
      setFileError(errorMessage(err));
    }
  });

  return (
    <Dialog title={`Add image — ${board.name}`} onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!file || submitting} onClick={submit}>{submitting ? "Uploading…" : "Upload"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {fileError && <Callout tone="error" title="Can't use this file">{fileError}</Callout>}
        <div onClick={() => inputRef.current?.click()} style={{ border: "2px dashed var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "var(--space-6)", textAlign: "center", cursor: "pointer" }}>
          <input ref={inputRef} type="file" accept={ALLOWED_IMAGE_TYPES.join(",")} style={{ display: "none" }} onChange={(e) => handleFile(e.target.files)} />
          <p style={{ font: "var(--type-label)", margin: 0 }}>{file ? file.name : "Click to choose an image"}</p>
          <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)", marginTop: 4 }}>JPEG, PNG, WebP or GIF — up to 15 MB.</p>
        </div>
        <Field label="Caption (optional)"><Input value={caption} onChange={(e) => setCaption(e.target.value)} /></Field>
        <Field label="Tags (comma separated)"><Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="blush, gold, rustic" /></Field>
      </div>
    </Dialog>
  );
}

function StyleProfileDialog({ eventId, profile, onClose, onSaved }: { eventId?: ID; profile?: StyleProfile; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [selectedEventId, setSelectedEventId] = React.useState(eventId || "");
  const [paletteColors, setPaletteColors] = React.useState((profile?.paletteColors || []).join(", "));
  const [styleTags, setStyleTags] = React.useState((profile?.styleTags || []).join(", "));
  const [notes, setNotes] = React.useState(profile?.notes || "");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const colors = paletteColors.split(",").map((c) => c.trim()).filter(Boolean);
    if (colors.some((c) => !/^#[0-9a-fA-F]{6}$/.test(c))) { setFormError("Colours must be hex codes like #F4E9DA."); return; }
    try {
      await service.moodboards.upsertStyleProfile({ eventId: selectedEventId || undefined, paletteColors: colors, styleTags: styleTags.split(",").map((t) => t.trim()).filter(Boolean), notes: notes || undefined });
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog title={eventId === undefined && !profile ? "Add celebration style" : "Edit style"} onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        {!profile && (
          <Field label="Celebration"><Select value={selectedEventId} placeholder="Whole wedding" onChange={(e) => setSelectedEventId(e.target.value)} options={workspace!.events.map((ev) => ({ value: ev.id, label: ev.name }))} /></Field>
        )}
        <Field label="Palette (hex codes, comma separated)"><Input value={paletteColors} onChange={(e) => setPaletteColors(e.target.value)} placeholder="#F4E9DA, #B08968" /></Field>
        <Field label="Style tags (comma separated)"><Input value={styleTags} onChange={(e) => setStyleTags(e.target.value)} placeholder="rustic, garden" /></Field>
        <Field label="Notes"><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}
